import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Star, Copy, Trash2, Printer, RefreshCw, Loader2, Pencil, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { ActivityDisplay } from "@/components/ActivityDisplay";
import { CenterDisplay, FamilyDisplay, BookDisplay } from "@/components/KindDisplay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ActivityView() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState({ en: "", es: "" });
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    api.get("/folders").then(({ data }) => setFolders([...data.default, ...data.custom.map((f) => f.name)])).catch(() => {});
  }, []);

  const assignFolder = async (f) => {
    await patch({ folder: f });
    toast.success(t("common.saved"));
  };

  const load = () => {
    setLoading(true);
    api.get(`/activities/${id}`)
      .then(({ data }) => { setActivity(data); setTitleDraft({ en: data.content.title_en || "", es: data.content.title_es || "" }); })
      .catch(() => toast.error("Activity not found"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/activities/${id}`, updates);
      setActivity(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleFav = () => patch({ is_favorite: !activity.is_favorite }).then((d) => d && toast.success(d.is_favorite ? t("common.favorited") : "Removed from favorites"));

  const saveTitle = async () => {
    const newContent = { ...activity.content, title_en: titleDraft.en, title_es: titleDraft.es };
    await patch({ content: newContent });
    setEditingTitle(false);
    toast.success(t("common.saved"));
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/activities/${id}/duplicate`);
      toast.success(t("common.duplicate") + " ✓");
      navigate(`/activities/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/activities/${id}`);
      toast.success(t("common.delete") + " ✓");
      navigate("/activities");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/activities/generate", activity.params);
      toast.success(t("activity.saved"));
      navigate(`/activities/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );
  }
  if (!activity) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 no-print">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> {t("common.back")}
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={toggleFav} disabled={busy} data-testid="fav-btn">
              <Star className={`h-4 w-4 ${activity.is_favorite ? "fill-secondary text-secondary" : ""}`} /> {t("common.favorite")}
            </Button>
            {activity.kind === "activity" && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={regenerate} disabled={busy} data-testid="regenerate-btn">
                <RefreshCw className="h-4 w-4" /> {t("common.regenerate")}
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-full" onClick={duplicate} disabled={busy} data-testid="duplicate-btn">
              <Copy className="h-4 w-4" /> {t("common.duplicate")}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.print()} data-testid="print-btn">
              <Printer className="h-4 w-4" /> {t("common.print")}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full text-destructive border-destructive/30" data-testid="delete-btn">
                  <Trash2 className="h-4 w-4" /> {t("common.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("common.delete")}?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">{t("common.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Title edit + folder */}
        <div className="no-print flex flex-wrap items-center gap-3">
          <Select value={activity.folder || undefined} onValueChange={assignFolder}>
            <SelectTrigger className="h-9 w-52 rounded-full" data-testid="folder-select"><SelectValue placeholder={t("common.saved") === "Guardado" ? "Mover a carpeta" : "Move to folder"} /></SelectTrigger>
            <SelectContent>
              {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="no-print">
          {editingTitle ? (
            <div className="space-y-2 rounded-2xl border border-border bg-card p-4">
              <Input value={titleDraft.en} onChange={(e) => setTitleDraft((d) => ({ ...d, en: e.target.value }))} placeholder="Title (English)" className="rounded-xl" data-testid="edit-title-en" />
              <Input value={titleDraft.es} onChange={(e) => setTitleDraft((d) => ({ ...d, es: e.target.value }))} placeholder="Título (Español)" className="rounded-xl" data-testid="edit-title-es" />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveTitle} disabled={busy} className="rounded-full bg-primary" data-testid="save-title"><Check className="h-4 w-4" /> {t("common.save")}</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)} className="rounded-full"><X className="h-4 w-4" /> {t("common.cancel")}</Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="text-muted-foreground rounded-full" onClick={() => setEditingTitle(true)} data-testid="edit-title-btn">
              <Pencil className="h-4 w-4" /> {t("common.edit")}
            </Button>
          )}
        </div>

        {activity.kind === "bilingual" && activity.original_text && (
          <div className="rounded-2xl border border-border bg-muted p-5 no-print">
            <h3 className="font-heading font-bold text-lg mb-2">{t("makeBilingual.original")}</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{activity.original_text}</p>
          </div>
        )}

        <div className="rounded-3xl border border-border bg-card p-6 sm:p-10">
          {activity.kind === "center" ? <CenterDisplay content={activity.content} t={t} />
            : activity.kind === "family" ? <FamilyDisplay content={activity.content} t={t} />
            : activity.kind === "book" ? <BookDisplay content={activity.content} t={t} />
            : <ActivityDisplay content={activity.content} params={activity.params} t={t} />}
        </div>
      </main>
    </div>
  );
}
